/**
 * PropertyMappingSelect — searchable dropdown for mapping CSV columns to CRM
 * properties, with an optional inline "Add new property" footer input.
 *
 * Follows the AddColumnHeader pattern (DialogTrigger + Popover + ListBox)
 * rather than the generic Select.ComboBox so we can include a sticky footer
 * action and section headers.
 */

import { Check, ChevronDown, Link01, Plus, SearchLg } from '@untitledui/icons';
import type { FC } from 'react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { Selection } from 'react-aria-components';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger,
  Input as AriaInput,
  ListBox,
  ListBoxItem,
  SearchField,
  TextField,
} from 'react-aria-components';

import { usePermissions } from '@/hooks/usePermissions';
import {
  CHEVRON_SIZE_CLASSES,
  SELECT_TRIGGER_BASE,
  SELECT_TRIGGER_FOCUS,
  sizes,
} from '@/lib/base/forms/select/select';
import { Popover } from '@/lib/base/overlays/popover';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import { useImportVocabulary } from '../import-vocabulary';

// ============================================================================
// Types
// ============================================================================

export interface PropertyMappingOption {
  /** Unique value — corresponds to property_type_name, a sentinel, etc. */
  id: string;
  /** Human-readable label shown in the list and trigger. */
  label: string;
  /** Grouping key — options with the same group share a section header. */
  group: string;
  /** Section header displayed above the first item in this group. */
  groupLabel: string;
  /** Whether this option is disabled (e.g. stage already mapped). */
  isDisabled?: boolean;
  /** Help text rendered below the label in smaller muted text. */
  description?: string;
  /** Optional icon rendered before the label. */
  icon?: FC<{ className?: string }>;
}

export interface PropertyMappingSelectProps {
  /** The currently selected option id (empty string = nothing selected). */
  value: string;
  /** Called when the user picks an option (including the empty/skip choice). */
  onChange: (value: string) => void;
  /** Flat list of options — grouped by `group` key, rendered in array order. */
  options: PropertyMappingOption[];
  /** Label shown when nothing is selected. */
  placeholder?: string;
  /**
   * Whether to show the "Do not import" / skip option at the top of the
   * dropdown. Defaults to `true`. Set to `false` in multi-type mapping where
   * columns are removed via the per-row action menu instead.
   */
  showSkipOption?: boolean;
  /**
   * Called when the user submits a new property name via the inline footer
   * input. When provided, a "+ Add new property" CTA appears at the bottom
   * of the dropdown; clicking it reveals a text field.
   */
  onCreateProperty?: (label: string) => void;
  /**
   * Pre-filled value for the inline "Add new property" input — the CSV column
   * header, which is almost always the label the user wants. Pre-selected on
   * focus so typing replaces it.
   */
  newPropertyDefaultLabel?: string;
  /**
   * Called when the user clicks "Link to record" in the footer. When provided,
   * the button renders in the footer before "Add new property".
   */
  onLinkToRecord?: () => void;
  /** Label shown in the trigger when a link-to-record value is active. */
  linkToRecordLabel?: string;
  /**
   * Accessible name for the trigger button. Use to provide column-specific
   * context when multiple selects appear in the same list, e.g.
   * `Select property for column First Name`.
   */
  ariaLabel?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/** Group adjacent options by their `group` key, preserving array order. */
function groupOptions(
  options: PropertyMappingOption[],
  query: string
): { group: string; groupLabel: string; items: PropertyMappingOption[] }[] {
  const q = query.toLowerCase().trim();
  const filtered = q
    ? options.filter(o => o.label.toLowerCase().includes(q))
    : options;

  const groups: {
    group: string;
    groupLabel: string;
    items: PropertyMappingOption[];
  }[] = [];
  let current: (typeof groups)[number] | null = null;

  for (const opt of filtered) {
    if (!current || current.group !== opt.group) {
      current = { group: opt.group, groupLabel: opt.groupLabel, items: [] };
      groups.push(current);
    }
    current.items.push(opt);
  }
  return groups;
}

// ============================================================================
// Component
// ============================================================================

export const PropertyMappingSelect = memo(function PropertyMappingSelect({
  value,
  onChange,
  options,
  placeholder = 'Do not import',
  showSkipOption = true,
  onCreateProperty,
  newPropertyDefaultLabel,
  onLinkToRecord,
  linkToRecordLabel = 'Link to record',
  ariaLabel,
}: PropertyMappingSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newPropertyLabel, setNewPropertyLabel] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const { isSchemaAdmin } = usePermissions();
  const { addPropertyDisabledTooltip } = useImportVocabulary();

  const grouped = useMemo(
    () => groupOptions(options, searchQuery),
    [options, searchQuery]
  );

  const isLinkActive = onLinkToRecord && value.startsWith('__link');

  const selectedLabel = useMemo(() => {
    if (!value) {
      return null;
    }
    // When in link-to-record mode, show the dedicated label instead of
    // formatting the sentinel value.
    if (isLinkActive) {
      return linkToRecordLabel;
    }
    const opt = options.find(o => o.id === value);
    if (opt) {
      return opt.label;
    }
    // Format raw property name into a human-readable fallback.
    // e.g. "org_custom_field" → "Custom Field"
    const withoutPrefix = value.startsWith('org_') ? value.slice(4) : value;
    const formatted = withoutPrefix
      .split('_')
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return formatted || value;
  }, [options, value, isLinkActive, linkToRecordLabel]);

  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      const key = [...keys][0];
      if (key !== undefined) {
        onChange(String(key));
      }
      setIsOpen(false);
      setSearchQuery('');
      setIsCreating(false);
      setNewPropertyLabel('');
    },
    [onChange]
  );

  const handleSkip = useCallback(() => {
    onChange('');
    setIsOpen(false);
    setSearchQuery('');
    setIsCreating(false);
    setNewPropertyLabel('');
  }, [onChange]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Focus the search input after the popover renders, using
      // preventScroll to avoid the browser auto-scrolling the
      // overflow container when the input receives focus.
      requestAnimationFrame(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      });
    } else {
      setSearchQuery('');
      setIsCreating(false);
      setNewPropertyLabel('');
    }
  }, []);

  const handleLinkToRecord = useCallback(() => {
    onLinkToRecord?.();
    setIsOpen(false);
    setSearchQuery('');
  }, [onLinkToRecord]);

  const handleStartCreate = useCallback(() => {
    setIsCreating(true);
    setNewPropertyLabel(newPropertyDefaultLabel ?? '');
    // Focus the input on next tick after it renders, selecting the pre-filled
    // column name so the user can accept it (Enter) or type straight over it.
    requestAnimationFrame(() => {
      createInputRef.current?.focus();
      createInputRef.current?.select();
    });
  }, [newPropertyDefaultLabel]);

  const handleSubmitNewProperty = useCallback(() => {
    const trimmed = newPropertyLabel.trim();
    if (!trimmed || !onCreateProperty) {
      return;
    }
    onCreateProperty(trimmed);
    setIsOpen(false);
    setSearchQuery('');
    setIsCreating(false);
    setNewPropertyLabel('');
  }, [newPropertyLabel, onCreateProperty]);

  const handleCreateKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmitNewProperty();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsCreating(false);
        setNewPropertyLabel('');
      }
    },
    [handleSubmitNewProperty]
  );

  const totalFiltered = useMemo(
    () => grouped.reduce((n, g) => n + g.items.length, 0),
    [grouped]
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      {/* Trigger button — shows current selection */}
      <AriaButton
        ref={triggerRef}
        aria-label={ariaLabel}
        className={cx(
          SELECT_TRIGGER_BASE,
          sizes.xs.root,
          'justify-between gap-2 text-left',
          'hover:bg-bg-secondary',
          isOpen && SELECT_TRIGGER_FOCUS,
          'focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]'
        )}
      >
        <span
          className={cx(
            'truncate',
            selectedLabel ? 'font-medium text-primary' : 'text-placeholder'
          )}
        >
          {selectedLabel ?? placeholder}
        </span>
        <ChevronDown
          aria-hidden='true'
          className={cx(
            'ml-auto shrink-0 text-fg-quaternary',
            CHEVRON_SIZE_CLASSES.xs
          )}
        />
      </AriaButton>

      {/* Popover dropdown */}
      {/* size='md' gives the max-h-80 panel; surface, ring, animation, z-index
          and StackingLayer come from the base Popover. */}
      <Popover
        size='md'
        triggerRef={triggerRef}
        placement='bottom start'
        className='w-(--trigger-width) min-w-56 overflow-hidden py-0'
      >
        {/* The Dialog takes the id DialogTrigger stamps into the trigger's
            aria-controls and owns focus + Escape — the non-modal Popover root
            is not a dialog, so without it both would be lost. */}
        <AriaDialog
          aria-label='Map column to property'
          className='flex flex-col max-h-80 outline-hidden'
        >
          {/* Search */}
          <div className='shrink-0 p-2 border-b border-border-secondary'>
            <SearchField
              value={searchQuery}
              onChange={setSearchQuery}
              aria-label='Search properties'
              className='w-full'
            >
              <div className='relative'>
                <SearchLg className='absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-fg-quaternary pointer-events-none' />
                <AriaInput
                  ref={searchInputRef}
                  placeholder='Search properties'
                  className={cx(
                    'w-full pl-8 pr-3 py-1.5 text-sm rounded-md',
                    'bg-bg-secondary text-fg-primary',
                    'placeholder:text-fg-tertiary',
                    'border border-border-secondary',
                    'outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-transparent'
                  )}
                />
              </div>
            </SearchField>
          </div>

          {/* Options list */}
          <div className='overflow-y-auto flex-1 min-h-0 py-1'>
            {/* Skip / Do not import — visible outside search when enabled */}
            {showSkipOption && !searchQuery && (
              <div className='px-1.5 py-px'>
                <AriaButton
                  onPress={handleSkip}
                  className={cx(
                    'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm cursor-pointer',
                    'hover:bg-primary_hover transition-colors duration-100',
                    value === ''
                      ? 'font-medium text-fg-primary'
                      : 'text-fg-secondary'
                  )}
                >
                  <span className='flex-1 text-left'>{placeholder}</span>
                  {value === '' && (
                    <Check
                      aria-hidden='true'
                      className='size-4 shrink-0 stroke-[2.5px] text-[var(--color-fg-brand-primary)]'
                    />
                  )}
                </AriaButton>
              </div>
            )}

            {totalFiltered === 0 && searchQuery ? (
              <div className='px-3 py-2 text-sm text-fg-tertiary'>
                No properties match &ldquo;{searchQuery}&rdquo;
              </div>
            ) : (
              grouped.map(section => (
                <div key={section.group}>
                  <div className='px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-fg-quaternary'>
                    {section.groupLabel}
                  </div>
                  <ListBox
                    aria-label={section.groupLabel}
                    selectionMode='single'
                    selectedKeys={value ? [value] : []}
                    onSelectionChange={handleSelectionChange}
                    disabledKeys={section.items
                      .filter(i => i.isDisabled)
                      .map(i => i.id)}
                    className='outline-none'
                  >
                    {section.items.map(item => (
                      <ListBoxItem
                        key={item.id}
                        id={item.id}
                        textValue={item.label}
                        className='px-1.5 py-px outline-none'
                      >
                        {({ isFocused, isHovered, isSelected, isDisabled }) => (
                          <div
                            className={cx(
                              'flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors duration-100',
                              isDisabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer',
                              (isFocused || isHovered) &&
                                !isDisabled &&
                                'bg-primary_hover',
                              isSelected && 'bg-active'
                            )}
                          >
                            {item.icon && (
                              <item.icon className='size-4 text-fg-quaternary shrink-0' />
                            )}
                            <div className='flex-1 min-w-0 text-left'>
                              <span
                                className={cx(
                                  'block truncate text-sm',
                                  isSelected
                                    ? 'font-medium text-fg-primary'
                                    : 'text-fg-secondary'
                                )}
                              >
                                {item.label}
                              </span>
                              {item.description && (
                                <span className='block text-xs text-fg-tertiary'>
                                  {item.description}
                                </span>
                              )}
                            </div>
                            {isSelected && (
                              <Check
                                aria-hidden='true'
                                className='size-4 shrink-0 stroke-[2.5px] text-[var(--color-fg-brand-primary)]'
                              />
                            )}
                          </div>
                        )}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </div>
              ))
            )}
          </div>

          {/* Footer — Link to record + Add new property */}
          {(onLinkToRecord || onCreateProperty) && (
            <div className='shrink-0 border-t border-border-secondary p-1.5'>
              {isCreating ? (
                <div className='flex items-center gap-1.5 px-1'>
                  <TextField
                    value={newPropertyLabel}
                    onChange={setNewPropertyLabel}
                    aria-label='New property name'
                    className='flex-1 min-w-0'
                  >
                    <AriaInput
                      ref={createInputRef}
                      placeholder='Property name'
                      onKeyDown={handleCreateKeyDown}
                      className={cx(
                        'w-full px-2.5 py-1.5 text-sm rounded-md',
                        'bg-bg-secondary text-fg-primary',
                        'placeholder:text-fg-tertiary',
                        'border border-border-secondary',
                        'outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)] focus:border-transparent'
                      )}
                    />
                  </TextField>
                  <AriaButton
                    onPress={handleSubmitNewProperty}
                    isDisabled={!newPropertyLabel.trim()}
                    className={cx(
                      'shrink-0 px-2.5 py-1.5 text-sm font-medium rounded-md transition-colors duration-100',
                      newPropertyLabel.trim()
                        ? 'text-[var(--color-fg-brand-primary)] hover:bg-primary_hover cursor-pointer'
                        : 'text-fg-disabled cursor-not-allowed'
                    )}
                  >
                    Add
                  </AriaButton>
                </div>
              ) : (
                <>
                  {onLinkToRecord && (
                    <AriaButton
                      onPress={handleLinkToRecord}
                      className={cx(
                        'w-full flex items-center gap-2 px-2.5 py-2 rounded-md',
                        'text-sm font-medium text-fg-secondary',
                        'hover:bg-primary_hover transition-colors duration-100 cursor-pointer'
                      )}
                    >
                      <Link01 className='size-4' />
                      {linkToRecordLabel}
                    </AriaButton>
                  )}
                  {onCreateProperty &&
                    (isSchemaAdmin ? (
                      <AriaButton
                        onPress={handleStartCreate}
                        className={cx(
                          'w-full flex items-center gap-2 px-2.5 py-2 rounded-md',
                          'text-sm font-medium text-[var(--color-fg-brand-primary)]',
                          'hover:bg-primary_hover transition-colors duration-100 cursor-pointer'
                        )}
                      >
                        <Plus className='size-4' />
                        Add new property
                      </AriaButton>
                    ) : (
                      <Tooltip
                        title={addPropertyDisabledTooltip}
                        placement='top'
                      >
                        <TooltipTrigger
                          aria-disabled='true'
                          // Focus ring via box-shadow (`ring-*`), not `outline-*`: TooltipTrigger's
                          // base `outline-hidden` sets --tw-outline-style:none, which
                          // `focus-visible:outline-2` inherits, so an outline never renders.
                          className='w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm font-medium text-fg-disabled cursor-not-allowed focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2'
                        >
                          <Plus className='size-4' />
                          Add new property
                        </TooltipTrigger>
                      </Tooltip>
                    ))}
                </>
              )}
            </div>
          )}
        </AriaDialog>
      </Popover>
    </DialogTrigger>
  );
});
